import { useRef, useState } from 'react';
import type React from 'react';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import type { SelectInputOption, SelectInputProps } from './selectInput';

type UseSelectInputArgs = Pick<
  SelectInputProps,
  | 'name'
  | 'onChange'
  | 'index'
  | 'handleClickAdd'
  | 'onKeyDown'
  | 'forceCloseMenu'
  | 'couldForceCloseMenu'
>;

const useSelectInput = ({
  name,
  onChange,
  index,
  handleClickAdd = () => {},
  onKeyDown: onKeyDownProp = () => {},
  forceCloseMenu = false,
  couldForceCloseMenu,
}: UseSelectInputArgs) => {
  const selectInputRef = useRef<any>(null);
  // react-select v5 always drives its inner menu-open state off this prop once it's
  // set at all, so it has to be tracked here to support couldForceCloseMenu below.
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  /**
   * Reports a new select choice to the owning form. The field name, the chosen option or options and the row index are passed straight through to the change handler.
   *
   * @param value - The selected option, list of options, or null when cleared.
   * @returns Nothing.
   */
  const handleChange = (value: SelectInputOption | SelectInputOption[] | null) => {
    onChange(name, value, index);
  };

  /**
   * Triggers the add action attached to the select control. The externally supplied add handler is invoked with no arguments.
   *
   * @returns Nothing.
   */
  const onClickAdd = () => {
    handleClickAdd();
  };

  const onSelectMenuOpen = () => setMenuIsOpen(true);
  const onSelectMenuClose = () => setMenuIsOpen(false);

  useUpdateEffect(() => {
    if (couldForceCloseMenu) {
      setMenuIsOpen(false);
    }
  }, [forceCloseMenu, couldForceCloseMenu]);

  /**
   * Forwards keyboard input from the select only while its option menu is closed. Keys typed while the menu is open stay with the select.
   *
   * @param e - Keyboard event from the select control.
   * @returns Nothing.
   */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!menuIsOpen) {
      onKeyDownProp(e);
    }
  };

  return {
    selectInputRef,
    menuIsOpen,
    onSelectMenuOpen,
    onSelectMenuClose,
    handleChange,
    onClickAdd,
    onKeyDown,
  };
};

export default useSelectInput;
